import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { images } from 'next/dist/build/webpack/config/blocks/images';

import { stories } from '../../content/Stories';
import type { StoryType } from '../../content/Stories';

import classes from './storyPopup.module.scss';

import ArrowSVG from '@/assets/svg/arrrow-popup.svg';
import CloseSVG from '@/assets/svg/close-popup.svg';
import Image from '@/components/UI/Image/Image';
import { TagList } from '@/components/UI/TagList/TagList';
import { useStorage } from '@/models/Storage';
import { CustomPagination } from '@/components/UI/Pagination/Pagination';
import { Video } from '@/components/Video/Video';
import { Like } from '@/components/UI/Like/Like';

export const StoryPopup = observer(({ likes }) => {
    const swiperRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const handlePagination = (index: number) => {
        swiperRef.current.swiper.slideTo(index);
    };

    const { activeStory, setActiveStory, addViewedStory } = useStorage();

    const [story, setStory] = useState<StoryType>({
        id: 0,
        images: [],
        preview: '',
        tags: [],
        text: [],
        pixel: '',
    });
    const [storyIndex, setStoryIndex] = useState(0);

    useEffect(() => {
        if (activeStory) {
            stories.forEach((storyObj, index) => {
                if (storyObj.id === activeStory) {
                    setStory(storyObj);
                    setStoryIndex(index);
                    addViewedStory(storyObj.id);
                }
            });
        }
    }, [activeStory]);

    const switchStory = (term = 0) => {
        const targetIndex = storyIndex + term;
        const targetStory = stories[targetIndex];
        if (targetStory) {
            setActiveStory(targetStory.id);
        }
    };

    return (
        <div className={classes.story}>
            <div className={classes.overlay} />
            <div className={clsx('container', classes.container)}>
                <button
                    className={clsx(classes.prev, storyIndex === 0 && classes.hidden)}
                    onClick={() => {
                        switchStory(-1);
                    }}
                >
                    <ArrowSVG />
                </button>
                <div className={classes.content}>
                    <CloseSVG
                        onClick={() => {
                            setActiveStory(undefined);
                        }}
                        className={classes.close}
                    />
                    <div className={classes.media}>
                        {story?.images.length > 1 ? (
                            <Swiper
                                onInit={(instance) => {
                                    setActiveSlide(instance.activeIndex);
                                }}
                                onSlideChange={(swiper) => {
                                    setActiveSlide(swiper.activeIndex);
                                }}
                                ref={swiperRef}
                            >
                                {story.images.map((imageData, index) => {
                                    return (
                                        <SwiperSlide key={'spim' + imageData.src?.src}>
                                            {imageData.type === 'image' ? (
                                                <Image src={imageData.src.src} />
                                            ) : (
                                                <Video
                                                    source={imageData.src}
                                                    poster={imageData.poster}
                                                    forcedPause={index !== activeSlide}
                                                    type="popup"
                                                />
                                            )}
                                        </SwiperSlide>
                                    );
                                })}
                                <CustomPagination
                                    className={classes.pagination}
                                    slidesAmount={story.images.length}
                                    currentIndex={activeSlide}
                                    onClick={handlePagination}
                                />
                            </Swiper>
                        ) : story.images[0]?.type === 'image' ? (
                            <Image src={story.images[0]?.src} />
                        ) : (
                            <Video
                                source={story.images[0]?.src}
                                poster={story.images[0]?.poster}
                                type="popup"
                            />
                        )}
                    </div>
                    <div className={classes.textBlock}>
                        <span className={classes.title}>{story?.preview}</span>
                        <div className={classes.text}>
                            <div className={classes.textWrapper}>
                                {story.fullText
                                    ? story.fullText
                                    : story?.text.map((block) => {
                                          return block;
                                      })}
                            </div>
                        </div>
                        <TagList
                            callback={() => {
                                setActiveStory(undefined);
                            }}
                            customTags={story.tags}
                            tagClassName={'small'}
                            className={'small'}
                        />
                        <Like key={story.id} storyId={story.id} amount={likes} />
                    </div>
                </div>
                <button
                    className={clsx(
                        classes.next,
                        storyIndex === stories.length - 1 && classes.hidden,
                    )}
                    onClick={() => {
                        switchStory(1);
                    }}
                >
                    <ArrowSVG />
                </button>
            </div>
        </div>
    );
});
